import React, { useState } from 'react';
import { InputBase } from '@mui/material';
import { notify } from '../../../notifications';
import { EditField, formatNumber, isIntField, parseNumberInput } from './editCalc';

interface NumberInputProps {
  value: number;
  field: EditField;
  /** Вызывается с новым числом после ввода (в режиме live - на каждый корректный ввод) */
  onCommit: (value: number) => void;
  /** Расчетное или внешнее значение: показывается, но не редактируется */
  readOnly?: boolean;
  disabled?: boolean;
  /** Значение изменено пользователем и еще не сохранено */
  changed?: boolean;
  /** Расчетное значение, изменившееся после пересчета */
  derived?: boolean;
  /** Выделить значение (итоговые поля) */
  strong?: boolean;
  /** Пересчитывать зависимые значения по мере набора, а не после выхода из поля */
  live?: boolean;
  /** Координаты поля для навигации с клавиатуры */
  row?: number;
  col?: number;
}

const RAW_ALLOWED = /[^\d\-−.,\s]/g;

/**
 * Числовое поле отчета: в покое показывает значение с разделителями разрядов, при вводе - обычный текст.
 * Пустое поле означает 0, некорректный ввод откатывается к прежнему значению с подсказкой.
 * Таблица месяца использует собственную облегченную ячейку (`MonthGrid.tsx`).
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  value, field, onCommit, readOnly, disabled, changed, derived, strong, live, row, col
}) => {
  const [text, setText] = useState<string | null>(null);
  const editing = text !== null;
  const display = editing ? (text as string) : formatNumber(value, field);
  const locked = Boolean(readOnly || disabled);

  const finish = () => {
    if (text === null) return;
    const parsed = parseNumberInput(text, field);
    setText(null);
    if (parsed.ok) {
      if (parsed.value !== value) onCommit(parsed.value);
    } else {
      notify.warning(`${field.label}: ${parsed.message}`);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(RAW_ALLOWED, '');
    setText(raw);
    if (live) {
      const parsed = parseNumberInput(raw, field);
      if (parsed.ok && parsed.value !== value) onCommit(parsed.value);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (locked) return;
    const el = e.target;
    setText(String(value));
    window.requestAnimationFrame(() => el.select());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  };

  let background = '#ffffff';
  let borderColor = '#e2e8f0';
  let color = '#1e293b';
  if (locked) {
    background = '#f8fafc';
    color = '#475569';
  }
  if (changed) {
    background = '#fffbeb';
    borderColor = '#fcd34d';
  } else if (derived) {
    color = '#2563eb';
  }

  return (
    <InputBase
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={finish}
      onKeyDown={handleKeyDown}
      readOnly={locked}
      disabled={disabled}
      fullWidth
      inputProps={{
        inputMode: isIntField(field) ? 'numeric' : 'decimal',
        'aria-label': field.label,
        'data-r': row,
        'data-c': col,
        tabIndex: readOnly ? -1 : 0,
        style: { textAlign: 'right' }
      }}
      sx={{
        height: 38,
        px: 1.5,
        borderRadius: '8px',
        border: '1px solid',
        borderColor,
        backgroundColor: background,
        color,
        fontSize: '0.92rem',
        fontWeight: strong ? 700 : 500,
        fontVariantNumeric: 'tabular-nums',
        transition: 'border-color 0.12s, box-shadow 0.12s, background-color 0.12s',
        cursor: locked ? 'default' : 'text',
        '&:hover': locked ? undefined : { borderColor: changed ? '#fbbf24' : '#cbd5e1' },
        '&.Mui-focused': locked ? undefined : {
          borderColor: '#2563eb',
          backgroundColor: '#ffffff',
          boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)'
        },
        '&.Mui-disabled': { color: '#94a3b8', WebkitTextFillColor: '#94a3b8' },
        '& input': { p: 0 }
      }}
    />
  );
};

export default NumberInput;
